import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Music, Users, Mail, Send, Eye, Download, Headphones, ArrowRight } from 'lucide-react';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    samples: { total: 0, active: 0, totalSent: 0, totalListens: 0, totalDownloads: 0 },
    subscribers: { total: 0, active: 0, totalDownloads: 0, totalEmailsSent: 0 },
    emails: { totalSent: 0, totalOpened: 0, totalListened: 0, totalDownloaded: 0, openRate: '0%', downloadRate: '0%', listenRate: '0%' }
  });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [samplesRes, subscribersRes, emailsRes, sampleAnalyticsRes] = await Promise.all([
        api.get('/samples/stats'),
        api.get('/subscribers/stats'),
        api.get('/email/stats'),
        api.get('/analytics').catch(() => ({ data: { analytics: {} } }))
      ]);

      const sampleAnalytics = sampleAnalyticsRes.data.analytics || {};

      setStats({
        samples: {
          ...samplesRes.data.stats,
          totalSent: sampleAnalytics.totalSent || 0,
          totalListens: sampleAnalytics.totalListens || 0,
          totalDownloads: sampleAnalytics.totalDownloads || 0
        },
        subscribers: subscribersRes.data.stats,
        emails: emailsRes.data.stats
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendDaily = async () => {
    if (!window.confirm('Send daily emails to all active subscribers?')) {
      return;
    }

    setSending(true);
    setMessage('');

    try {
      const response = await api.post('/email/send-daily');
      setMessage(`Success! Sent ${response.data.results.sent} emails. Failed: ${response.data.results.failed}`);
      fetchStats();
    } catch (error) {
      setMessage('Error sending emails: ' + (error.response?.data?.message || error.message));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Dashboard Overview</h2>
        <button 
          className="send-now-btn" 
          onClick={handleSendDaily}
          disabled={sending}
        >
          <Send size={20} />
          {sending ? 'Sending...' : 'Send Daily Emails Now'}
        </button>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {/* Samples Section */}
      <div className="section-header">
        <h3>📀 Samples Overview</h3>
        <button className="quick-access-btn" onClick={() => navigate('/samples')}>
          View All <ArrowRight size={16} />
        </button>
      </div>
      <div className="stats-grid">
        <div className="stat-card gradient-purple">
          <div className="stat-icon">
            <Music size={32} />
          </div>
          <div className="stat-content">
            <h3>Total Samples</h3>
            <p className="stat-number">{stats.samples.total}</p>
            <p className="stat-detail">{stats.samples.active} active</p>
          </div>
        </div>

        <div className="stat-card gradient-blue">
          <div className="stat-icon">
            <Send size={32} />
          </div>
          <div className="stat-content">
            <h3>Total Sent</h3>
            <p className="stat-number">{stats.samples.totalSent}</p>
            <p className="stat-detail">Sample emails sent</p>
          </div>
        </div>

        <div className="stat-card gradient-teal">
          <div className="stat-icon">
            <Headphones size={32} />
          </div>
          <div className="stat-content">
            <h3>Total Listens</h3>
            <p className="stat-number">{stats.samples.totalListens}</p>
            <p className="stat-detail">Samples listened to</p>
          </div>
        </div>

        <div className="stat-card gradient-green">
          <div className="stat-icon">
            <Download size={32} />
          </div>
          <div className="stat-content">
            <h3>Total Downloads</h3>
            <p className="stat-number">{stats.samples.totalDownloads}</p>
            <p className="stat-detail">Samples downloaded</p>
          </div>
        </div>
      </div>

      {/* Subscribers Section */}
      <div className="section-header">
        <h3>👥 Subscribers Overview</h3>
        <button className="quick-access-btn" onClick={() => navigate('/subscribers')}>
          View All <ArrowRight size={16} />
        </button>
      </div>
      <div className="stats-grid">
        <div className="stat-card gradient-pink">
          <div className="stat-icon">
            <Users size={32} />
          </div>
          <div className="stat-content">
            <h3>Total Subscribers</h3>
            <p className="stat-number">{stats.subscribers.total}</p>
            <p className="stat-detail">{stats.subscribers.active} active</p>
          </div>
        </div>

        <div className="stat-card gradient-orange">
          <div className="stat-icon">
            <Download size={32} />
          </div>
          <div className="stat-content">
            <h3>Total Downloads</h3>
            <p className="stat-number">{stats.subscribers.totalDownloads || 0}</p>
            <p className="stat-detail">By all subscribers</p>
          </div>
        </div>

        <div className="stat-card gradient-purple">
          <div className="stat-icon">
            <Mail size={32} />
          </div>
          <div className="stat-content">
            <h3>Total Emails</h3>
            <p className="stat-number">{stats.subscribers.totalEmailsSent || 0}</p>
            <p className="stat-detail">Sent to subscribers</p>
          </div>
        </div>
      </div>

      {/* Email Tracking Section */}
      <div className="section-header">
        <h3>📧 Email Campaign Stats</h3>
        <button className="quick-access-btn" onClick={() => navigate('/tracking')}>
          View Tracking <ArrowRight size={16} />
        </button>
      </div>
      <div className="stats-grid">
        <div className="stat-card gradient-blue">
          <div className="stat-icon">
            <Mail size={32} />
          </div>
          <div className="stat-content">
            <h3>Emails Sent</h3>
            <p className="stat-number">{stats.emails.totalSent}</p>
            <p className="stat-detail">Total campaigns</p>
          </div>
        </div>

        <div className="stat-card gradient-cyan">
          <div className="stat-icon">
            <Eye size={32} />
          </div>
          <div className="stat-content">
            <h3>Opened</h3>
            <p className="stat-number">{stats.emails.totalOpened || 0}</p>
            <p className="stat-detail">Open rate: {stats.emails.openRate}</p>
          </div>
        </div>

        <div className="stat-card gradient-teal">
          <div className="stat-icon">
            <Headphones size={32} />
          </div>
          <div className="stat-content">
            <h3>Listened</h3>
            <p className="stat-number">{stats.emails.totalListened || 0}</p>
            <p className="stat-detail">Listen rate: {stats.emails.listenRate || '0%'}</p>
          </div>
        </div>

        <div className="stat-card gradient-green">
          <div className="stat-icon">
            <Download size={32} />
          </div>
          <div className="stat-content">
            <h3>Downloaded</h3>
            <p className="stat-number">{stats.emails.totalDownloaded || 0}</p>
            <p className="stat-detail">Download rate: {stats.emails.downloadRate}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;



